import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { financialService } from '../services/financialService'

type Outcome = 'checking' | 'completed' | 'pending' | 'failed'

export default function PaymentCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [outcome, setOutcome] = useState<Outcome>('checking')
  const [message, setMessage] = useState('Confirming your payment with PesaPal…')
  const orderTrackingId = searchParams.get('OrderTrackingId') || searchParams.get('orderTrackingId')

  useEffect(() => {
    if (!orderTrackingId) {
      setOutcome('failed')
      setMessage('No PesaPal order reference was returned. If money was deducted, contact the school bursar with your reference.')
      return
    }
    let cancelled = false
    const poll = async (attempt: number) => {
      try {
        const status = await financialService.getOnlinePaymentStatus(orderTrackingId)
        if (cancelled) return
        if (status.status === 'completed') {
          setOutcome('completed')
          setMessage(`Payment of UGX ${Number(status.amount).toLocaleString()} received. Remaining balance: UGX ${Number(status.balance_due).toLocaleString()}.`)
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          setOutcome('failed')
          setMessage('PesaPal reported this payment as unsuccessful. No funds were applied to the invoice.')
        } else if (attempt < 5) {
          setTimeout(() => poll(attempt + 1), 2000)
        } else {
          setOutcome('pending')
          setMessage('PesaPal has not confirmed this payment yet. It can take a few minutes — check back on your portal shortly.')
        }
      } catch {
        if (!cancelled) {
          setOutcome('failed')
          setMessage('We could not confirm this payment. If money was deducted, contact the school bursar with your PesaPal reference.')
        }
      }
    }
    poll(0)
    return () => { cancelled = true }
  }, [orderTrackingId])

  return (
    <div className="mx-auto mt-16 max-w-md">
      <div className="card">
        <div className="card-content flex flex-col items-center gap-4 py-10 text-center">
          {outcome === 'checking' && <Loader2 className="h-12 w-12 animate-spin text-primary-600" />}
          {outcome === 'completed' && <CheckCircle className="h-12 w-12 text-green-600" />}
          {(outcome === 'failed' || outcome === 'pending') && <XCircle className="h-12 w-12 text-yellow-600" />}
          <h1 className="text-xl font-semibold">
            {outcome === 'completed' ? 'Payment successful' : outcome === 'checking' ? 'Confirming payment…' : 'Payment not yet confirmed'}
          </h1>
          <p className="text-sm text-gray-600">{message}</p>
          {orderTrackingId && <p className="text-xs text-gray-400">Reference: {orderTrackingId}</p>}
          <button onClick={() => navigate('/portal')} className="btn btn-primary mt-2">
            Return to portal
          </button>
        </div>
      </div>
    </div>
  )
}
